import { useState, useMemo } from 'react'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'

interface WeeklyDataPoint {
  week_number: number
  week_start: string
  week_end: string
  total_count: number
  leasing_count: number
  naerings_leasing_count?: number
  privat_leasing_count?: number
  undefined_leasing_count?: number
}

interface Props {
  data: WeeklyDataPoint[]
  prevYearData: WeeklyDataPoint[]
  year: number
}

const COLORS = {
  leasing: '#8b5cf6',        // purple
  naeringsLeasing: '#f97316', // orange
  privatLeasing: '#10b981',   // green
  prevLeasing: '#c4b5fd',     // light purple (prev year)
}

const nbNO = (n: number) =>
  n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })

const pctNO = (n: number) =>
  (n * 100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %'

const fmtDate = (d: string) => {
  const parts = d.split('-')
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d
}

type ChartMode = 'andel' | 'antall' | 'akkumulert'

export default function LeasingWeeklyChart({ data, prevYearData, year }: Props) {
  const { t } = useTranslation('components')
  const { isDark } = useTheme()
  const [chartMode, setChartMode] = useState<ChartMode>('andel')
  const prevYear = year - 1

  const prevByWeek = useMemo(() => {
    const map: Record<number, WeeklyDataPoint> = {}
    for (const d of prevYearData) map[d.week_number] = d
    return map
  }, [prevYearData])

  const chartData = useMemo(() => {
    const weekNumbers = data.map((d) => d.week_number).sort((a, b) => a - b)
    const currentByWeek: Record<number, WeeklyDataPoint> = {}
    for (const d of data) currentByWeek[d.week_number] = d

    let accLeasing = 0
    let accPrevLeasing = 0

    // Bound here: inside the map below, `t` is the week's total count.
    const weekLabel = (n: number) => t('charts.weekLabel', { n })

    return weekNumbers.map((wn) => {
      const cur = currentByWeek[wn]
      const prev = prevByWeek[wn]
      const t = cur?.total_count || 1

      accLeasing += cur?.leasing_count || 0
      accPrevLeasing += prev?.leasing_count || 0

      return {
        week_number: wn,
        label: weekLabel(wn),
        week_start: cur?.week_start || '',
        week_end: cur?.week_end || '',
        total_count: cur?.total_count || 0,
        // Rates
        leasing_rate: cur ? cur.leasing_count / t : null,
        naerings_leasing_rate: cur ? (cur.naerings_leasing_count || 0) / t : null,
        privat_leasing_rate: cur ? (cur.privat_leasing_count || 0) / t : null,
        // Counts
        leasing_count: cur?.leasing_count || 0,
        naerings_leasing_count: cur?.naerings_leasing_count || 0,
        privat_leasing_count: cur?.privat_leasing_count || 0,
        prev_leasing_count: prev?.leasing_count ?? null,
        // Accumulated
        acc_leasing: accLeasing,
        acc_prev_leasing: accPrevLeasing > 0 ? accPrevLeasing : null,
      }
    })
  }, [data, prevYearData, prevByWeek, t])

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const raw = payload[0]?.payload as any

    return (
      <div className="rounded-lg shadow-lg border p-3 text-xs bg-white dark:bg-dark-surface dark:border-dark-border max-w-xs">
        <p className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">
          {label} ({fmtDate(raw.week_start)} – {fmtDate(raw.week_end)})
        </p>
        {chartMode !== 'akkumulert' && raw.total_count > 0 && (
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {t('charts.totalRegistrations', { n: nbNO(raw.total_count) })}
          </p>
        )}
        <div className="space-y-1">
          {chartMode === 'andel' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.leasing }}>● {t('leasingWeeklyChart.leasingShare')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.leasing_rate != null ? pctNO(raw.leasing_rate) : '–'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.naeringsLeasing }}>● {t('leasingWeeklyChart.businessLeasing')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.naerings_leasing_rate != null ? pctNO(raw.naerings_leasing_rate) : '–'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.privatLeasing }}>● {t('leasingWeeklyChart.privateLeasing')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.privat_leasing_rate != null ? pctNO(raw.privat_leasing_rate) : '–'}</span>
            </div>
          </>
          )}
          {chartMode === 'antall' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.leasing }}>● {t('leasingWeeklyChart.leasingYear', { year })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.leasing_count)}</span>
            </div>
            {raw.prev_leasing_count != null && (
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.prevLeasing }}>● {t('leasingWeeklyChart.leasingYear', { year: prevYear })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.prev_leasing_count)}</span>
            </div>
            )}
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.naeringsLeasing }}>● {t('leasingWeeklyChart.businessLeasing')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.naerings_leasing_count)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.privatLeasing }}>● {t('leasingWeeklyChart.privateLeasing')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.privat_leasing_count)}</span>
            </div>
          </>
          )}
          {chartMode === 'akkumulert' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.leasing }}>● {t('leasingWeeklyChart.leasingYear', { year })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_leasing)}</span>
            </div>
            {raw.acc_prev_leasing != null && (
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.prevLeasing }}>● {t('leasingWeeklyChart.leasingYear', { year: prevYear })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_prev_leasing)}</span>
            </div>
            )}
          </>
          )}
        </div>
      </div>
    )
  }

  const subtitle: Record<ChartMode, string> = {
    andel: t('leasingWeeklyChart.subtitleShare'),
    antall: t('leasingWeeklyChart.subtitleCount'),
    akkumulert: t('leasingWeeklyChart.subtitleAccumulated', { year, prevYear }),
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('leasingWeeklyChart.title', { year })}
        </h2>
        <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          {(['andel', 'antall', 'akkumulert'] as ChartMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setChartMode(mode)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                chartMode === mode
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
              }`}
            >
              {mode === 'andel' ? t('charts.share') : mode === 'antall' ? t('charts.count') : t('charts.accumulated')}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {subtitle[chartMode]}
      </p>

      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={Math.max(0, Math.floor(chartData.length / 13) - 1)}
          />

          {chartMode === 'andel' && (
          <YAxis
            yAxisId="ratio"
            orientation="left"
            domain={[0, 'auto']}
            tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{ value: t('charts.share'), angle: -90, position: 'insideLeft', style: { fill: axisColor, fontSize: 12 } }}
          />
          )}

          {(chartMode === 'antall' || chartMode === 'akkumulert') && (
          <YAxis
            yAxisId="count"
            orientation="right"
            domain={[0, 'auto']}
            tickFormatter={(v: number) => nbNO(v)}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{ value: chartMode === 'akkumulert' ? t('charts.accumulated') : t('charts.count'), angle: 90, position: 'insideRight', style: { fill: axisColor, fontSize: 12 } }}
          />
          )}

          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} iconType="circle" />

          {/* Andel mode */}
          {chartMode === 'andel' && (
          <>
            <Line yAxisId="ratio" type="monotone" dataKey="leasing_rate" name={t('leasingWeeklyChart.leasingShare')} stroke={COLORS.leasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="ratio" type="monotone" dataKey="naerings_leasing_rate" name={t('leasingWeeklyChart.businessLeasing')} stroke={COLORS.naeringsLeasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="ratio" type="monotone" dataKey="privat_leasing_rate" name={t('leasingWeeklyChart.privateLeasing')} stroke={COLORS.privatLeasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
          </>
          )}

          {/* Antall mode */}
          {chartMode === 'antall' && (
          <>
            <Line yAxisId="count" type="monotone" dataKey="leasing_count" name={t('leasingWeeklyChart.leasingYear', { year })} stroke={COLORS.leasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="count" type="monotone" dataKey="prev_leasing_count" name={t('leasingWeeklyChart.leasingYear', { year: prevYear })} stroke={COLORS.prevLeasing} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
            <Line yAxisId="count" type="monotone" dataKey="naerings_leasing_count" name={t('leasingWeeklyChart.businessLeasing')} stroke={COLORS.naeringsLeasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="count" type="monotone" dataKey="privat_leasing_count" name={t('leasingWeeklyChart.privateLeasing')} stroke={COLORS.privatLeasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
          </>
          )}

          {/* Akkumulert mode */}
          {chartMode === 'akkumulert' && (
          <>
            <Line yAxisId="count" type="monotone" dataKey="acc_leasing" name={t('leasingWeeklyChart.leasingYear', { year })} stroke={COLORS.leasing} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="count" type="monotone" dataKey="acc_prev_leasing" name={t('leasingWeeklyChart.leasingYear', { year: prevYear })} stroke={COLORS.prevLeasing} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
          </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
